import { useState } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';

import LockIconBase from '#/assets/LockIcon';
import CheckmarkIconBase from '#/assets/CheckmarkIcon';
import GlobeIconBase from '#/assets/GlobeIcon';
import ParadexIcon from '#/assets/ParadexIcon';
import WalletConnectIcon from '#/assets/WalletConnectIcon';

import { c00EE91, cCC38FF, cFFFFFF } from '#/colors';

import UsernameEditModal from '#/components/account/UsernameEditModal';
import { HorizontalSpacer } from '#/components/shared/Spacer';
import * as Alert from '#/components/common/Alert';

import {
  useWalletModalActions,
  useWalletModalView,
} from '#/features/wallet/modal-context';
import { useWalletView } from '#/features/wallet/wallet-context';
import { useAuthActions } from '#/features/auth/auth-context';
import { useBuildStarknetContractUrl } from '#/features/starknet';
import { UsernameEditModalProvider } from '#/features/account/username-edit-modal-context';
import StarknetWallet from '#/features/starknet-wallet/wallet';
import EthereumWallet from '#/features/ethereum-wallet/wallet';
import { logException } from '#/features/logging/logging';
import { FeatureFlags } from '#/features/feature-flags';
import { useTranslation } from '#/features/localization/utils';

import useCopyToClipboard from '#/utils/useCopyToClipboard';
import { LocationUtil } from '#/utils/location';

import * as Modal from './Modal';
import PrimaryButton from './PrimaryButton';
import { AddressBlock } from './AddressBlock';
import UsernameSection from './UsernameSection';

export default function WalletModal() {
  const [isBusy, setBusy] = useState(false);

  const walletView = useWalletView();
  const authActions = useAuthActions();
  const modalActions = useWalletModalActions();
  const { t } = useTranslation();

  const buildStarknetContractUrl = useBuildStarknetContractUrl();

  const signOut = () => {
    setBusy(true);
    authActions.reset();
    modalActions.close();
    EthereumWallet.disconnect()
      .catch((err) => {
        const message = 'Unexpected error signing user out';
        logException(new Error(message, { cause: err }));
      })
      .finally(() => {
        LocationUtil.reload();
      });
  };

  if (!walletView.isSignedIn) {
    return (
      <WalletModalBase>
        <Alert.Error>Wallet not connected</Alert.Error>
      </WalletModalBase>
    );
  }

  return (
    <WalletModalBase>
      {FeatureFlags.getBooleanValue('account-usernames-enabled', false) && (
        <UsernameSection />
      )}
      <Section aria-labelledby="account-dialog-wallets-section-title">
        <SectionTitle id="account-dialog-wallets-section-title">
          {t('Wallets')}
        </SectionTitle>
        {walletView.ethereumAddress != null && (
          <AddressBlock
            title={t('L1 Address')}
            address={walletView.ethereumAddress}
            icon={<WalletConnectIcon size={32} />}
          />
        )}

        {walletView.starknetAddress != null && (
          <Group>
            <AddressBlock
              title={t('L2 Address')}
              address={walletView.starknetAddress}
              icon={<ParadexIcon size={32} />}
            />
            <CopyPrivateKeyLink />
            <HorizontalSpacer />
            <Link
              href={buildStarknetContractUrl(walletView.starknetAddress)}
              target="_blank"
              rel="nofollow noopener"
            >
              {t('Open in Explorer')}
              <GlobeIcon />
            </Link>
          </Group>
        )}
      </Section>

      <SignOutButton onClick={signOut} disabled={isBusy}>
        {t('Sign Out')}
      </SignOutButton>
    </WalletModalBase>
  );
}

function WalletModalBase({ children }: React.PropsWithChildren<unknown>) {
  const modalView = useWalletModalView();
  const modalActions = useWalletModalActions();
  const { t } = useTranslation();

  return (
    <Modal.Root isOpen={modalView.isOpen} onDismiss={modalActions.close}>
      <Modal.Header>
        <Modal.Title css="margin-bottom: 6px;">{t('Account')}</Modal.Title>
        <Modal.CloseButton onClick={modalActions.close} />
      </Modal.Header>

      <UsernameEditModalProvider>
        {children}
        <UsernameEditModal />
      </UsernameEditModalProvider>
    </Modal.Root>
  );
}

function CopyPrivateKeyLink() {
  // Get private key directly from StarknetWallet to
  // avoid storing it in memory where it's not required.
  const privateKey = StarknetWallet.getPrivateKey();
  const { t } = useTranslation();

  const [copyToClipboard, status] = useCopyToClipboard(privateKey);

  return (
    <Link as="button" type="button" onClick={copyToClipboard}>
      {t('Copy L2 Private Key')}
      {status === 'idle' ? <LockIcon /> : <CheckmarkIcon />}
    </Link>
  );
}

const Section = styled.section`
  display: grid;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  color: ${rgba(cFFFFFF, 0.7)};
  font-size: 12px;
  font-weight: 500;
  margin: 0;
`;

const Link = styled.a`
  align-items: center;
  border: none;
  background: none;
  display: grid;
  gap: 5px;
  grid-template-columns: 1fr auto;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  text-align: left;
  padding: 8px 16px;

  color: ${rgba(cFFFFFF, 0.7)};

  &:hover {
    color: ${rgba(cFFFFFF, 0.85)};
  }

  &:active {
    color: ${cFFFFFF};
  }
`;

const SignOutButton = styled(PrimaryButton)``;

const Group = styled.div`
  display: grid;
  gap: 8px;
`;

const LockIcon = styled(LockIconBase)`
  color: ${cCC38FF};
`;

const CheckmarkIcon = styled(CheckmarkIconBase)`
  color: ${c00EE91};
`;

const GlobeIcon = styled(GlobeIconBase)`
  color: ${cCC38FF};
`;
